import type { Pcg32 } from '../../sim/rng';
import type { Palette } from '../palette';
import { MeshBuilder, place } from './MeshBuilder';

/** Every kit builder has this shape: palette in, seeded variety in, triangles out (05 §5). */
export type KitBuilder = (p: Palette, rng: Pcg32) => MeshBuilder;

const TAU = Math.PI * 2;

/** Conifer: stacked cones. In the arctic each tier carries a snow cap. */
export const pine: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const height = rng.range(5.2, 7.6);
  const tiers = height > 6.4 ? 4 : 3;
  const trunkH = height * 0.2;
  mb.cylinder(0.2, 0.15, trunkH + 0.4, 5, p.color('timber.lo'));
  const snowy = p.biome === 'arctic';
  let y = trunkH;
  for (let i = 0; i < tiers; i++) {
    const t = i / (tiers - 1);
    const r = (1.75 - t * 1.0) * rng.range(0.92, 1.08);
    const h = (height - trunkH) / tiers + 0.75;
    const yaw = rng.range(0, TAU);
    mb.cylinder(r, 0, h, 7, p.color(i % 2 === 0 ? 'foliage.lo' : 'foliage.mid'), place(0, y, 0, yaw));
    if (snowy) {
      mb.cylinder(r * 0.74, 0, h * 0.74, 7, p.color('ground.hi'), place(0, y + h * 0.27, 0, yaw));
    }
    y += (height - trunkH) / tiers;
  }
  return mb;
};

/** Deciduous tree: trunk and a crown of three lumpy blobs. */
export const broadleaf: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const trunkH = rng.range(1.6, 2.4);
  mb.cylinder(0.3, 0.2, trunkH + 0.6, 5, p.color('timber.lo'));
  const crown = rng.range(1.5, 2.0);
  const blobs: Array<[number, number, number, number]> = [
    [0, trunkH + crown * 0.8, 0, 1],
    [crown * 0.65, trunkH + crown * 0.45, crown * 0.2, 0.72],
    [-crown * 0.6, trunkH + crown * 0.55, -crown * 0.15, 0.78],
    [crown * 0.05, trunkH + crown * 0.4, crown * 0.6, 0.66],
  ];
  for (const [x, y, z, s] of blobs) {
    mb.blob(crown * s, crown * s * 0.78, crown * s, p.color('foliage.mid'), place(x, y, z, rng.range(0, TAU)), {
      jitter: 0.16,
      rng,
      topColor: p.color('foliage.hi'),
    });
  }
  return mb;
};

/** Bare, broken tree: good silhouette against snow and sand. */
export const deadTree: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const h = rng.range(2.6, 3.8);
  const col = p.color('timber.lo');
  mb.cylinder(0.22, 0.1, h, 5, col);
  for (let i = 0; i < 3; i++) {
    const yaw = rng.range(0, TAU);
    const at = h * rng.range(0.45, 0.85);
    mb.cylinder(0.07, 0.03, rng.range(0.9, 1.5), 4, col, place(0, at, 0, yaw, 1, rng.range(0.7, 1.1)));
  }
  return mb;
};

/** Low cover a crawling ranger can hide behind. */
export const shrub: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const n = rng.int(2) + 2;
  for (let i = 0; i < n; i++) {
    const r = rng.range(0.55, 0.9);
    mb.blob(r, r * 0.7, r, p.color('foliage.mid'), place(rng.range(-0.5, 0.5), r * 0.25, rng.range(-0.4, 0.4), rng.range(0, TAU)), {
      jitter: 0.2,
      rng,
      topColor: p.color(p.biome === 'arctic' ? 'ground.hi' : 'foliage.hi'),
      flatBottom: true,
    });
  }
  return mb;
};

export interface RockParams {
  size?: number;
}

/** Boulder. Destroys pods, stops rockets, hides rangers. */
export function rock(p: Palette, rng: Pcg32, params: RockParams = {}): MeshBuilder {
  const mb = new MeshBuilder();
  const s = params.size ?? rng.range(0.7, 1.6);
  const top = p.biome === 'arctic' ? p.color('ground.hi') : p.color('rock.hi');
  mb.blob(s * rng.range(0.9, 1.3), s * rng.range(0.6, 0.9), s * rng.range(0.8, 1.1), p.color('rock.mid'), place(0, s * 0.18, 0, rng.range(0, TAU)), {
    jitter: 0.24,
    rng,
    topColor: top,
    flatBottom: true,
  });
  if (rng.chance(0.6)) {
    const k = s * 0.5;
    mb.blob(k, k * 0.7, k, p.color('rock.lo'), place(s * rng.range(0.7, 1.0), k * 0.15, s * rng.range(-0.3, 0.5)), {
      jitter: 0.25,
      rng,
      topColor: top,
      flatBottom: true,
    });
  }
  return mb;
}

/** Saguaro-style cactus. */
export const cactus: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const h = rng.range(2.0, 3.0);
  const body = p.color('foliage.mid');
  const lit = p.color('foliage.hi');
  mb.cylinder(0.26, 0.22, h, 6, body, undefined, lit);
  const arms = rng.int(2) + 1;
  for (let i = 0; i < arms; i++) {
    const side = i === 0 ? 1 : -1;
    const at = h * rng.range(0.35, 0.55);
    const reach = rng.range(0.5, 0.7);
    mb.box(reach, 0.3, 0.3, body, place((side * reach) / 2, at, 0));
    mb.cylinder(0.17, 0.15, h * rng.range(0.3, 0.45), 6, body, place(side * reach, at, 0), lit);
  }
  return mb;
};

/** Oasis palm: leaning segmented trunk and drooping fronds. */
export const palm: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const lean = rng.range(0.08, 0.2);
  const dir = rng.range(0, TAU);
  let x = 0;
  let z = 0;
  let y = 0;
  const segs = 5;
  const segH = rng.range(0.9, 1.15);
  for (let i = 0; i < segs; i++) {
    const r = 0.2 - i * 0.02;
    mb.cylinder(r, r - 0.02, segH + 0.05, 5, p.color(i % 2 ? 'timber.mid' : 'timber.lo'), place(x, y, z));
    y += segH;
    x += Math.sin(dir) * lean * (i + 1) * 0.35;
    z += Math.cos(dir) * lean * (i + 1) * 0.35;
  }
  const fronds = 7;
  for (let i = 0; i < fronds; i++) {
    const yaw = (i / fronds) * TAU + rng.range(-0.2, 0.2);
    const len = rng.range(1.9, 2.5);
    // A frond is a long shallow tent, tipped downward away from the crown.
    mb.with(place(x, y, z, yaw, 1, 0, 0), () => {
      mb.gable(len, 0.16, 0.7, p.color(i % 2 ? 'foliage.mid' : 'foliage.hi'), place(len / 2, -0.25, 0, 0, 1, 0, -0.42));
    });
  }
  mb.blob(0.3, 0.3, 0.3, p.color('timber.mid'), place(x, y - 0.1, z), { detail: 0 });
  return mb;
};

/** A few blades: instanced clutter, never outlined. */
export const grassTuft: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const n = rng.int(3) + 3;
  for (let i = 0; i < n; i++) {
    const tone = rng.chance(0.5) ? 'foliage.hi' : 'foliage.mid';
    mb.cylinder(0.07, 0, rng.range(0.3, 0.55), 3, p.color(tone), place(rng.range(-0.22, 0.22), 0, rng.range(-0.18, 0.18), rng.range(0, TAU), 1, rng.range(-0.2, 0.2)));
  }
  return mb;
};
